import {Banner, IconButton} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Input} from 'semantic-ui-react';

import {trackSetting} from '../../services/analytics/workspaceSettingsEvents';
import {type RunHistoryKeyInfo} from '../../types/run';
import {type RunsLinePlotConfig} from '../PanelRunsLinePlot/types';
import {
  getSelectedMetricCount,
  getTruncatedSelectedMetrics,
  REGEX_METRIC_MAX_LEN,
} from '../PanelRunsLinePlot/utils';
import {useTrackSettingPage} from '../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {type MetricsPickerProps, MetricsRegexToggle} from './MetricsPicker';

const REGEX_METRIC_PREVIEW_DEBOUNCE_MS = 150;
const REGEX_METRIC_CONFIG_DEBOUNCE_MS = 750;
const REGEX_METRIC_PREVIEW_INITIAL_LEN = 5;
const REGEX_METRIC_PREVIEW_SHOW_MORE_LEN = 20;

type RegexMetricPreviewData = {
  count: number;
  metrics: string[];
};

export const MetricsPickerRegexInput = (props: MetricsPickerProps) => {
  const {keyInfo, config, updateConfig} = props;
  const trackSettingPage = useTrackSettingPage();
  const latestProps = useRef({config, keyInfo, updateConfig});
  latestProps.current = {config, keyInfo, updateConfig};

  const [inputRegex, setInputRegex] = useState(config.metricRegex);
  const [metricPreview, setMetricPreview] = useState<RegexMetricPreviewData>(
    () => getRegexMetricPreview(config.metricRegex, keyInfo)
  );
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const [numVisibleMetrics, setNumVisibleMetrics] = useState(
    REGEX_METRIC_PREVIEW_INITIAL_LEN
  );

  // Keep the text input responsive. Metric matching scans every history key in
  // large workspaces, so doing it directly in onChange blocks keystrokes. The
  // shorter debounce updates only local preview state, while the longer config
  // debounce waits for a stronger idle signal before kicking off global chart
  // query/render work.
  const updatePreviewDebounced = useMemo(
    () =>
      debounce(
        (newRegex: string | undefined, newKeyInfo: RunHistoryKeyInfo) => {
          setMetricPreview(getRegexMetricPreview(newRegex, newKeyInfo));
        },
        REGEX_METRIC_PREVIEW_DEBOUNCE_MS
      ),
    []
  );

  const updateConfigDebounced = useMemo(
    () =>
      debounce((newRegex: string | undefined) => {
        const {
          config: latestConfig,
          keyInfo: latestKeyInfo,
          updateConfig: latestUpdateConfig,
        } = latestProps.current;
        const preview = getRegexMetricPreview(newRegex, latestKeyInfo);
        const newConfig: Partial<RunsLinePlotConfig> = {};

        if (newRegex !== latestConfig.metricRegex) {
          newConfig.metricRegex = newRegex;
        }

        // Avoid a no-op metrics write. Even if the regex text changed,
        // writing a value-identical metrics array still fans out through
        // Redux and rebuilds line data for the panel.
        if (!stringArraysEqual(preview.metrics, latestConfig.metrics)) {
          newConfig.metrics = preview.metrics;
        }

        if (Object.keys(newConfig).length > 0) {
          // intentionally omitting `useMetricRegex` since that gets populated
          // when user clicks on the regex button already
          latestUpdateConfig(newConfig);
        }
      }, REGEX_METRIC_CONFIG_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    return () => {
      updatePreviewDebounced.cancel();
      updateConfigDebounced.cancel();
    };
  }, [updateConfigDebounced, updatePreviewDebounced]);

  const onInputChanged = useCallback(
    (_, {value: newRegex}) => {
      setInputRegex(newRegex);
      setIsPreviewExpanded(newRegex.length > 0);
      setNumVisibleMetrics(REGEX_METRIC_PREVIEW_INITIAL_LEN);
      updatePreviewDebounced(newRegex, keyInfo);
      updateConfigDebounced(newRegex);
    },
    [keyInfo, updateConfigDebounced, updatePreviewDebounced]
  );

  const onInputFocused = useCallback(() => {
    setIsPreviewExpanded(inputRegex != null && inputRegex.length > 0);
    setNumVisibleMetrics(REGEX_METRIC_PREVIEW_INITIAL_LEN);
  }, [inputRegex]);

  const onInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.currentTarget.blur();
      }
    },
    []
  );

  const onInputBlurred = useCallback(() => {
    updatePreviewDebounced.flush();
    updateConfigDebounced.flush();
    setIsPreviewExpanded(false);
    trackSetting({
      action: 'change',
      page: trackSettingPage,
      settingLevel: 'panel',
      setting: 'yAxis',
      settingValue: inputRegex?.toString(),
      additionalProperties: {
        isInputRegexEmpty: !inputRegex?.length,
      },
    });
  }, [
    inputRegex,
    trackSettingPage,
    updateConfigDebounced,
    updatePreviewDebounced,
  ]);

  const onClearRegex = useCallback(() => {
    updatePreviewDebounced.cancel();
    updateConfigDebounced.cancel();
    setInputRegex('');
    setMetricPreview(getRegexMetricPreview(undefined, keyInfo));
    setIsPreviewExpanded(false);
    setNumVisibleMetrics(REGEX_METRIC_PREVIEW_INITIAL_LEN);

    const newConfig: Partial<RunsLinePlotConfig> = {};
    if (config.metricRegex !== undefined) {
      newConfig.metricRegex = undefined;
    }
    if (!stringArraysEqual([], config.metrics)) {
      newConfig.metrics = [];
    }
    if (Object.keys(newConfig).length > 0) {
      updateConfig(newConfig);
    }
  }, [
    config.metricRegex,
    config.metrics,
    keyInfo,
    updateConfig,
    updateConfigDebounced,
    updatePreviewDebounced,
  ]);

  const onTogglePreviewExpanded = useCallback(() => {
    setIsPreviewExpanded(prev => !prev);
  }, []);

  const onShowMoreMetrics = useCallback(() => {
    setNumVisibleMetrics(
      current => current + REGEX_METRIC_PREVIEW_SHOW_MORE_LEN
    );
  }, []);

  const onShowLessMetrics = useCallback(() => {
    setNumVisibleMetrics(REGEX_METRIC_PREVIEW_INITIAL_LEN);
  }, []);

  return (
    <div className="flex w-full min-w-0 flex-col gap-12">
      <div className="metrics-picker">
        <div className="metrics-picker-regex-input-wrapper">
          <Input
            aria-label="Metric regex"
            style={{flexGrow: 1}}
            className="regex-input"
            placeholder="Select metrics with a regular expression"
            value={inputRegex ?? ''}
            onChange={onInputChanged}
            onFocus={onInputFocused}
            onBlur={onInputBlurred}
            onKeyDown={onInputKeyDown}
          />
          <div
            className="metrics-picker-regex-actions"
            onMouseDown={keepRegexActionsFromBlurringInput}>
            <MetricsRegexToggle {...props} />
            <IconButton
              aria-label="Clear regex input"
              icon="close"
              onPress={onClearRegex}
              size="md"
              variant="ghost"
            />
          </div>
        </div>
      </div>
      {inputRegex != null && inputRegex.length > 0 && (
        <RegexMetricPreview
          preview={metricPreview}
          isExpanded={isPreviewExpanded}
          numVisibleMetrics={numVisibleMetrics}
          onShowLessMetrics={onShowLessMetrics}
          onShowMoreMetrics={onShowMoreMetrics}
          onToggleExpanded={onTogglePreviewExpanded}
        />
      )}
    </div>
  );
};

function getRegexMetricPreview(
  metricRegex: string | undefined,
  keyInfo: RunHistoryKeyInfo
): RegexMetricPreviewData {
  const args = {
    metricRegex,
    keyInfo,
    sortedKeys: keyInfo.sortedKeys,
  };
  return {
    count: getSelectedMetricCount(args),
    metrics: getTruncatedSelectedMetrics(args),
  };
}

// This sits in the regex-edit hot path. lodash.isEqual would be correct, but
// it pays for generic deep comparison; here we only need ordered string arrays
// with cheap reference/null/length bailouts.
function stringArraysEqual(
  left: string[] | undefined,
  right: string[] | undefined
) {
  if (left === right) {
    return true;
  }
  if (left == null || right == null || left.length !== right.length) {
    return false;
  }
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) {
      return false;
    }
  }
  return true;
}

const RegexMetricPreview = ({
  preview,
  isExpanded,
  numVisibleMetrics,
  onShowLessMetrics,
  onShowMoreMetrics,
  onToggleExpanded,
}: {
  preview: RegexMetricPreviewData;
  isExpanded: boolean;
  numVisibleMetrics: number;
  onShowLessMetrics: () => void;
  onShowMoreMetrics: () => void;
  onToggleExpanded: () => void;
}) => {
  const isMetricLimitExceeded = preview.count > REGEX_METRIC_MAX_LEN;
  const displayedMetrics = preview.metrics.slice(0, numVisibleMetrics);
  const numMoreMetrics = preview.metrics.length - displayedMetrics.length;
  const numMetricsToShowMore = Math.min(
    REGEX_METRIC_PREVIEW_SHOW_MORE_LEN,
    numMoreMetrics
  );
  const hasExpandedContent = isExpanded || isMetricLimitExceeded;
  const hasMoreThanInitialMetrics =
    preview.metrics.length > REGEX_METRIC_PREVIEW_INITIAL_LEN;

  return (
    <TailwindContents>
      <div
        className="rounded-md bg-moon-100 px-14 py-10 text-base text-moon-800"
        onMouseDown={event => event.preventDefault()}
        data-test="regex-metric-preview">
        <div
          className={`flex items-center justify-between gap-8 ${
            hasExpandedContent ? 'mb-8' : ''
          }`}>
          <div className="font-semibold text-moon-700">
            {preview.count.toLocaleString()} metrics matched
          </div>
          {preview.metrics.length > 0 && (
            <IconButton
              aria-label={
                isExpanded ? 'Hide metric matches' : 'Show metric matches'
              }
              icon={isExpanded ? 'chevron-up' : 'chevron-down'}
              onPress={onToggleExpanded}
              size="sm"
              variant="ghost"
            />
          )}
        </div>
        {isMetricLimitExceeded && (
          <Banner
            className={isExpanded ? 'mb-8' : ''}
            isDismissible={false}
            size="sm"
            variant="warning">
            Only first {REGEX_METRIC_MAX_LEN} can be shown.
          </Banner>
        )}
        {isExpanded && preview.metrics.length > 0 && (
          <>
            <ul className="m-0 list-disc space-y-6 pl-24">
              {displayedMetrics.map(metric => (
                <li className="break-all leading-5" key={metric} title={metric}>
                  {metric}
                </li>
              ))}
            </ul>
            {numMoreMetrics > 0 ? (
              <button
                type="button"
                className="mt-8 text-left font-semibold text-teal-600 hover:text-teal-700"
                onClick={onShowMoreMetrics}>
                Show {numMetricsToShowMore} more
              </button>
            ) : (
              hasMoreThanInitialMetrics && (
                <button
                  type="button"
                  className="mt-8 text-left font-semibold text-teal-600 hover:text-teal-700"
                  onClick={onShowLessMetrics}>
                  Show less
                </button>
              )
            )}
          </>
        )}
      </div>
    </TailwindContents>
  );
};

function keepRegexActionsFromBlurringInput(
  event: React.MouseEvent<HTMLDivElement>
) {
  // Blur flushes the pending regex update. Keep action clicks from flushing a
  // stale regex before the clear/toggle handler runs.
  event.preventDefault();
}
